import { isDefined } from '@aloft/toolkit-shared';
import { Typography, TypographyProps } from '@aloft/toolkit-sui';
import {
  BoxProps,
  Table as ChakraTable,
  TableContainer as ChakraTableContainer,
  Tbody as ChakraTbody,
  Td as ChakraTd,
  Th as ChakraTh,
  Thead as ChakraThead,
  Tr as ChakraTr,
  TableBodyProps,
  TableCellProps,
  TableColumnHeaderProps,
  TableContainerProps,
  TableHeadProps,
  TableProps,
  TableRowProps,
  Tooltip,
  chakra,
} from '@aloft/toolkit-sui/chakra';

import { ReactNode, forwardRef } from 'react';

import { Caret } from './Caret.js';
import { READ_ONLY_BACKGROUND_COLOR } from './constants.js';

type HighlightProps = {
  isHighlighted?: boolean;
};

type BuiltinTypographyProps = { typographyProps?: Omit<TypographyProps, 'children'> };

type TooltippableCellProps = { tooltipContent?: ReactNode };

const makeHighlightProps = (isHighlighted: boolean | undefined): BoxProps => {
  return isHighlighted ? { backgroundColor: 'surfaceAccent4' } : {};
};

const makeReadonlyProps = (isReadonly: boolean | undefined): BoxProps =>
  isReadonly ? { backgroundColor: READ_ONLY_BACKGROUND_COLOR } : {};

export const Table = (p: TableProps) => {
  return (
    <ChakraTable
      variant={'unstyled'}
      size={'sm'}
      {...p}
      sx={{
        tr: {
          height: '25px',
        },
        'tr, td, th': {
          borderColor: 'strokeDefault',
          borderStyle: 'solid',
          borderWidth: 'thin',
        },
        'th, td': {
          paddingY: '.5',
          paddingX: '1',
        },
        ...p.sx,
      }}
    />
  );
};

export const Thead = (p: TableHeadProps) => {
  return <ChakraThead {...p} />;
};

export const Tbody = (p: TableBodyProps) => {
  return <ChakraTbody {...p} />;
};

export const Tr = (p: TableRowProps & HighlightProps & { isReadonly?: boolean }) => {
  const { isHighlighted, isReadonly, ...props } = p;
  return (
    <ChakraTr
      {...makeHighlightProps(isHighlighted)}
      {...makeReadonlyProps(isReadonly)}
      {...props}
    />
  );
};
export const Td = forwardRef(
  (
    p: TableCellProps &
      BuiltinTypographyProps &
      TooltippableCellProps & {
        type?: 'readonly' | 'editable';
        allowOverflow?: boolean;
      },
    ref,
  ) => {
    const { type, typographyProps, tooltipContent, allowOverflow, ...props } = p;
    const children =
      typeof p.children === 'string' ? (
        <Typography variant="body2" casing={'initial'} {...typographyProps}>
          {p.children}
        </Typography>
      ) : (
        p.children
      );
    return (
      <ChakraTd
        position={'relative'}
        overflow={allowOverflow ? undefined : 'hidden'}
        padding={type === 'editable' ? '0 !important' : 'initial'}
        verticalAlign={'middle'}
        {...makeReadonlyProps(type === 'readonly')}
        {...props}
        ref={ref}
      >
        {isDefined(tooltipContent) && <CellTooltip>{tooltipContent}</CellTooltip>}
        {children}
      </ChakraTd>
    );
  },
);

type ThProps = TableColumnHeaderProps &
  HighlightProps &
  BuiltinTypographyProps &
  TooltippableCellProps;
export const Th = forwardRef((p: ThProps, ref) => {
  const { isHighlighted, typographyProps, tooltipContent, ...props } = p;
  const children =
    typeof p.children === 'string' ? (
      <Typography variant="tableH1" casing={'initial'} {...typographyProps}>
        {p.children}
      </Typography>
    ) : (
      p.children
    );
  return (
    <ChakraTh {...makeHighlightProps(isHighlighted)} {...props} position={'relative'} ref={ref}>
      {isDefined(tooltipContent) && <CellTooltip>{tooltipContent}</CellTooltip>}
      {children}
    </ChakraTh>
  );
});

export const TableContainer = (p: TableContainerProps) => {
  return <ChakraTableContainer {...p} />;
};

function CellTooltip({ children }: { children: ReactNode }) {
  return (
    <Tooltip placement="top" openDelay={750} label={children}>
      <chakra.span
        width="24px"
        height="24px"
        display={'block'}
        position={'absolute'}
        top={0}
        right={0}
        cursor={'help'}
      >
        <Caret />
      </chakra.span>
    </Tooltip>
  );
}
